/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getAddressDecoder,
  getAddressEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const CHECK_AUTHORIZATION_DISCRIMINATOR = 14;

export function getCheckAuthorizationDiscriminatorBytes() {
  return getU8Encoder().encode(CHECK_AUTHORIZATION_DISCRIMINATOR);
}

export type CheckAuthorizationInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? ReadonlyAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type CheckAuthorizationInstructionData = {
  discriminator: number;
  address: Address;
};

export type CheckAuthorizationInstructionDataArgs = { address: Address };

export function getCheckAuthorizationInstructionDataEncoder(): FixedSizeEncoder<CheckAuthorizationInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['address', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: CHECK_AUTHORIZATION_DISCRIMINATOR })
  );
}

export function getCheckAuthorizationInstructionDataDecoder(): FixedSizeDecoder<CheckAuthorizationInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['address', getAddressDecoder()],
  ]);
}

export function getCheckAuthorizationInstructionDataCodec(): FixedSizeCodec<
  CheckAuthorizationInstructionDataArgs,
  CheckAuthorizationInstructionData
> {
  return combineCodec(
    getCheckAuthorizationInstructionDataEncoder(),
    getCheckAuthorizationInstructionDataDecoder()
  );
}

export type CheckAuthorizationInput<
  TAccountProgramAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
  address: CheckAuthorizationInstructionDataArgs['address'];
};

export function getCheckAuthorizationInstruction<
  TAccountProgramAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: CheckAuthorizationInput<TAccountProgramAuthority>,
  config?: { programAddress?: TProgramAddress }
): CheckAuthorizationInstruction<TProgramAddress, TAccountProgramAuthority> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [getAccountMeta(accounts.programAuthority)],
    data: getCheckAuthorizationInstructionDataEncoder().encode(
      args as CheckAuthorizationInstructionDataArgs
    ),
    programAddress,
  } as CheckAuthorizationInstruction<
    TProgramAddress,
    TAccountProgramAuthority
  >);
}

export type ParsedCheckAuthorizationInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
  };
  data: CheckAuthorizationInstructionData;
};

export function parseCheckAuthorizationInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedCheckAuthorizationInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { programAuthority: getNextAccount() },
    data: getCheckAuthorizationInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
